import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AgentsService,
  ToolContextDataService,
  type AgentTreeNodeDto,
} from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/inference-client';
import { ADMIN_QUERY_KEYS } from '@/features/admin/api/query-keys';
import { WORKSPACE_SCHEDULES_KEY } from '../useOpportunitySchedule';
import { useAuth } from '@/entities/user';
import { notificationService } from '@/lib/services/notification.service';
import {
  mergeToolSchemas,
  buildBatchSavePayload,
  type MergedSchema,
  type FieldMapping,
} from '../../lib/mergeToolSchemas';
import { getFullyConfiguredWorkspaceIds } from '../../lib/toolConfigCoverage';

interface UseAgentConfigParams {
  agentId: string;
}

const EMPTY_TOOL_CONFIG_SCOPES = new Map<string, ToolConfigScope>();

export interface ToolConfigScope {
  hasOrg: boolean;
  wsCount: number;
  workspaceIds: string[];
}

/** Patches isConfigured flags in the agent tree based on actual config data */
function patchTreeConfigStatus(
  node: AgentTreeNodeDto,
  toolConfigScopes: Map<string, ToolConfigScope>,
): AgentTreeNodeDto {
  return {
    ...node,
    tools: node.tools.map((tool) => {
      if (!tool.isConfigurable) return tool;
      const key = `${tool.toolId}:${tool.agentId}`;
      return { ...tool, isConfigured: toolConfigScopes.has(key) };
    }),
    children: node.children.map((child) => patchTreeConfigStatus(child, toolConfigScopes)),
  };
}

/** Collects unique tool+agent pairs from merged fields */
function getUniqueToolAgentPairs(merged: MergedSchema): FieldMapping[] {
  const seen = new Set<string>();
  const pairs: FieldMapping[] = [];
  for (const field of merged.fields) {
    for (const mapping of field.mappings) {
      const key = `${mapping.toolId}:${mapping.agentId}`;
      if (!seen.has(key)) {
        seen.add(key);
        pairs.push(mapping);
      }
    }
  }
  return pairs;
}

export interface WorkspaceConfig {
  id: string;
  workspaceId: string;
  data: Record<string, unknown>;
}

export function useAgentConfig({ agentId }: UseAgentConfigParams) {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();
  const organizationId = useAuth((s) => s.signedInUser.value?.organization.id ?? '');

  const agentQuery = useQuery({
    queryKey: ADMIN_QUERY_KEYS.tools.agentConfig(agentId),
    queryFn: async () => {
      const { data } = await AgentsService.getAgentById({
        client: inferenceHttp,
        path: { id: agentId },
      });
      return data!;
    },
    enabled: !!agentId,
  });

  const configStatus = agentQuery.data?.configurationStatus;
  const agentTree = configStatus?.agentTree ?? null;

  const merged: MergedSchema | null = useMemo(
    () => (agentTree ? mergeToolSchemas(agentTree) : null),
    [agentTree],
  );

  const toolAgentPairs = useMemo(() => (merged ? getUniqueToolAgentPairs(merged) : []), [merged]);

  // Fetch org + workspace configs for all tool+agent pairs
  const scopesQuery = useQuery({
    queryKey: [
      ...ADMIN_QUERY_KEYS.tools.agentWorkspaceConfigs(agentId),
      organizationId,
      toolAgentPairs,
    ],
    queryFn: async () => {
      const results = await Promise.all(
        toolAgentPairs.map(async (pair) => {
          const { data } = await ToolContextDataService.findAllByToolId({
            client: inferenceHttp,
            path: { toolId: pair.toolId },
            query: { organizationId, agentId: pair.agentId },
          });
          return { toolId: pair.toolId, agentId: pair.agentId, scopes: data };
        }),
      );

      // Extract org-level config values across tools
      const orgValues: Record<string, unknown> = {};
      let hasOrgConfig = false;
      for (const result of results) {
        const orgConfig = result.scopes?.orgConfig;
        if (orgConfig?.data) {
          hasOrgConfig = true;
          Object.assign(orgValues, orgConfig.data as Record<string, unknown>);
        }
      }

      // Merge workspace configs by workspaceId across tools
      const byWorkspace = new Map<string, { id: string; data: Record<string, unknown> }>();
      for (const result of results) {
        const wsConfigs = result.scopes?.workspaceConfigs ?? [];
        for (const wc of wsConfigs) {
          if (!wc.workspaceId) continue;
          const existing = byWorkspace.get(wc.workspaceId);
          if (existing) {
            existing.data = { ...existing.data, ...(wc.data as Record<string, unknown>) };
          } else {
            byWorkspace.set(wc.workspaceId, {
              id: wc.id,
              data: wc.data as Record<string, unknown>,
            });
          }
        }
      }

      const workspaceConfigs = Array.from(byWorkspace.entries()).map(
        ([workspaceId, { id, data }]): WorkspaceConfig => ({ id, workspaceId, data }),
      );

      // Track per-tool config scope info
      const toolConfigScopes = new Map<string, ToolConfigScope>();
      for (const result of results) {
        const key = `${result.toolId}:${result.agentId}`;
        const hasOrg = !!result.scopes?.orgConfig?.data;
        const wsConfigs = result.scopes?.workspaceConfigs ?? [];
        const workspaceIds = wsConfigs.map((c) => c.workspaceId).filter((id): id is string => !!id);
        const wsCount = workspaceIds.length;
        if (hasOrg || workspaceIds.length > 0) {
          toolConfigScopes.set(key, { hasOrg, wsCount, workspaceIds });
        }
      }

      // allHaveOrgConfig: every tool+agent pair has org config
      const allScopes = Array.from(toolConfigScopes.values());
      const allHaveOrgConfig =
        toolConfigScopes.size > 0 &&
        toolConfigScopes.size === toolAgentPairs.length &&
        allScopes.every((s) => s.hasOrg);

      const fullyConfiguredWorkspaceIds = getFullyConfiguredWorkspaceIds(
        toolAgentPairs,
        toolConfigScopes,
      );

      return {
        orgValues: hasOrgConfig ? orgValues : null,
        allHaveOrgConfig,
        fullyConfiguredWorkspaceIds,
        workspaceConfigs,
        toolConfigScopes,
      };
    },
    enabled: toolAgentPairs.length > 0 && !!organizationId,
  });

  const invalidateQueries = () => {
    void queryClient.invalidateQueries({
      queryKey: ADMIN_QUERY_KEYS.tools.agentConfig(agentId),
    });
    void queryClient.invalidateQueries({
      queryKey: ADMIN_QUERY_KEYS.tools.agentWorkspaceConfigs(agentId),
    });
    void queryClient.invalidateQueries({
      queryKey: ADMIN_QUERY_KEYS.tools.agents,
    });
    // Saving a configuration is what creates the workspace's job row on the server, so its
    // schedule only exists from this point on.
    void queryClient.invalidateQueries({
      queryKey: WORKSPACE_SCHEDULES_KEY,
    });
  };

  const saveMutation = useMutation({
    mutationFn: async (params: { formData: Record<string, unknown>; workspaceId?: string }) => {
      if (!merged) throw new Error('No merged schema available');

      const baseItems = buildBatchSavePayload(
        params.formData,
        merged.fields,
        organizationId,
        params.workspaceId,
      );
      const { data } = await ToolContextDataService.batchUpsert({
        client: inferenceHttp,
        body: { items: baseItems },
      });
      return data;
    },
    onSuccess: () => {
      invalidateQueries();
      notificationService.showSuccess(t('admin.useAgentConfig.configurationSaved'));
    },
    onError: () => {
      notificationService.showError(t('admin.useAgentConfig.failedToSaveConfiguration'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (params?: { workspaceId?: string }) => {
      if (!merged) throw new Error('No merged schema available');

      const currentScopes = scopesQuery.data?.toolConfigScopes;
      const seen = new Set<string>();
      const deletePromises: Promise<unknown>[] = [];
      for (const field of merged.fields) {
        for (const mapping of field.mappings) {
          const key = `${mapping.toolId}:${mapping.agentId}`;
          if (seen.has(key)) continue;
          seen.add(key);

          // Only delete pairs that actually have configs at the requested scope
          const scope = currentScopes?.get(key);
          if (!scope) continue;
          if (!params?.workspaceId && !scope.hasOrg) continue;
          if (params?.workspaceId && scope.wsCount === 0) continue;

          deletePromises.push(
            ToolContextDataService.deleteByToolId({
              client: inferenceHttp,
              path: { toolId: mapping.toolId },
              query: {
                organizationId,
                agentId: mapping.agentId,
                workspaceId: params?.workspaceId,
              },
            }),
          );
        }
      }
      await Promise.all(deletePromises);
    },
    onSuccess: () => {
      invalidateQueries();
      notificationService.showSuccess(t('admin.useAgentConfig.configurationRemoved'));
    },
    onError: () => {
      notificationService.showError(t('admin.useAgentConfig.failedToRemoveConfiguration'));
    },
  });

  const toolConfigScopes = scopesQuery.data?.toolConfigScopes ?? EMPTY_TOOL_CONFIG_SCOPES;
  const patchedAgentTree = useMemo(
    () => (agentTree ? patchTreeConfigStatus(agentTree, toolConfigScopes) : null),
    [agentTree, toolConfigScopes],
  );

  return {
    agent: agentQuery.data,
    agentTree: patchedAgentTree,
    toolConfigScopes,
    merged,
    orgValues: scopesQuery.data?.orgValues ?? null,
    allHaveOrgConfig: scopesQuery.data?.allHaveOrgConfig ?? false,
    fullyConfiguredWorkspaceIds: scopesQuery.data?.fullyConfiguredWorkspaceIds ?? [],
    workspaceConfigs: scopesQuery.data?.workspaceConfigs ?? [],
    isLoading: agentQuery.isLoading,
    save: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
    remove: deleteMutation.mutateAsync,
    isRemoving: deleteMutation.isPending,
  };
}
